import { useState, useMemo, useCallback } from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Fire, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { Spinner } from "@heroui/react";
import { useFireSafetyData } from "../hooks/useFireSafetyData";
import { FireSafetyTable } from "../components";
import { FireSafetySummaryCards } from "../components";
import { UniversalTableSearch, UniversalFilterSection } from "shared/components/UniversalTable";
import {
  SmokeDetectorsOnEveryFloorStatus,
  FireExtinguisherOnEveryFloorStatus,
  FireHoseReelStatus,
  EmergencyLightingOnEveryFloorStatus,
  FireProofDoorsStatus,
  FireEvacuationStatus,
  FireStairStatus
} from "shared/types/fire-security";

// Filter sections configuration
const FILTER_SECTIONS: UniversalFilterSection[] = [
  {
    key: 'type',
    label: 'Type',
    options: [
      { key: 'building', label: 'Gebouwen' },
      { key: 'unit', label: 'Units' }
    ],
    color: 'moss',
    multiSelect: true
  }
];

const BrandveiligheidPage = () => {
  const {
    fireSafetyInfo,
    isLoading,
    actionableIssues,
    totalItems,
    updateField,
    copyFieldToTargets,
    refresh,
    getStatusOptions
  } = useFireSafetyData();

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({ type: [] });
  const [activeActionableFilter, setActiveActionableFilter] = useState<string | null>(null);
  const [highlightedColumn, setHighlightedColumn] = useState<string | null>(null);

  // Sort states
  const [sortColumn, setSortColumn] = useState("location");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Handle filter change from UniversalTableSearch
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => ({ ...prev, [key]: values }));
    // Clear actionable filter when using regular filters
    if (values.length > 0) {
      setActiveActionableFilter(null);
      setHighlightedColumn(null);
    }
  }, []);

  // Handle actionable issue filter
  const handleActionableIssueFilter = useCallback((issueType: string, columnName?: string) => {
    setActiveFilters({ type: [] });
    setSearchTerm('');
    setHighlightedColumn(columnName || null);
    setActiveActionableFilter(issueType === 'clear' ? null : issueType);
  }, []);

  // Sort data function
  const sortData = useCallback((data: typeof fireSafetyInfo, column: string, direction: "asc" | "desc") => {
    return [...data].sort((a, b) => {
      let aValue: any = column === "location" ? a.formattedLocation : a[column as keyof typeof a];
      let bValue: any = column === "location" ? b.formattedLocation : b[column as keyof typeof b];

      // Handle date comparison
      if (column.includes("Date") && aValue && bValue) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue === null || aValue === undefined) return direction === "asc" ? 1 : -1;
      if (bValue === null || bValue === undefined) return direction === "asc" ? -1 : 1;

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, []);

  // Filter and sort data
  const filteredInfo = useMemo(() => {
    let filtered = [...fireSafetyInfo];

    if (activeActionableFilter) {
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      switch (activeActionableFilter) {
        case 'rookmelders':
          filtered = filtered.filter(item =>
            item.smokeDetectorsOnEveryFloorStatus === SmokeDetectorsOnEveryFloorStatus.NOT_OK
          );
          break;
        case 'brandblussers':
          filtered = filtered.filter(item =>
            item.fireExtinguisherOnEveryFloorStatus === FireExtinguisherOnEveryFloorStatus.NOT_OK
          );
          break;
        case 'brandhaspel':
          filtered = filtered.filter(item =>
            item.fireHoseReelStatus === FireHoseReelStatus.NOT_OK
          );
          break;
        case 'noodverlichting':
          filtered = filtered.filter(item =>
            item.emergencyLightingOnEveryFloorStatus === EmergencyLightingOnEveryFloorStatus.NOT_OK
          );
          break;
        case 'branddeuren':
          filtered = filtered.filter(item =>
            item.fireProofDoors === FireProofDoorsStatus.NOT_OK
          );
          break;
        case 'evacuatie':
          filtered = filtered.filter(item =>
            item.fireEvacuationStatus === FireEvacuationStatus.NOT_OK
          );
          break;
        case 'brandtrap':
          filtered = filtered.filter(item =>
            item.fireStairStatus === FireStairStatus.NOT_OK
          );
          break;
        case 'keuring':
          filtered = filtered.filter(item => {
            if (!item.fireSecurityNextInspectionDate) return false;
            const nextInspectionDate = new Date(item.fireSecurityNextInspectionDate);
            return nextInspectionDate <= thirtyDaysFromNow && nextInspectionDate >= today;
          });
          break;
        case 'keuringVervallen':
          filtered = filtered.filter(item => {
            if (!item.fireSecurityNextInspectionDate) return false;
            const nextInspectionDate = new Date(item.fireSecurityNextInspectionDate);
            return nextInspectionDate < today;
          });
          break;
        case 'inspectiedatumNietIngepland':
          filtered = filtered.filter(item => !item.fireSecurityNextInspectionDate);
          break;
        // Unknown status filters
        case 'rookmeldersOnbekend':
          filtered = filtered.filter(item =>
            item.smokeDetectorsOnEveryFloorStatus === SmokeDetectorsOnEveryFloorStatus.UNKNOWN
          );
          break;
        case 'brandblusersOnbekend':
          filtered = filtered.filter(item =>
            item.fireExtinguisherOnEveryFloorStatus === FireExtinguisherOnEveryFloorStatus.UNKNOWN
          );
          break;
        case 'brandhaspelOnbekend':
          filtered = filtered.filter(item =>
            item.fireHoseReelStatus === FireHoseReelStatus.UNKNOWN
          );
          break;
        case 'noodverlichtingOnbekend':
          filtered = filtered.filter(item =>
            item.emergencyLightingOnEveryFloorStatus === EmergencyLightingOnEveryFloorStatus.UNKNOWN
          );
          break;
        case 'brandeurenOnbekend':
          filtered = filtered.filter(item =>
            item.fireProofDoors === FireProofDoorsStatus.UNKNOWN
          );
          break;
        case 'evacuatieOnbekend':
          filtered = filtered.filter(item =>
            item.fireEvacuationStatus === FireEvacuationStatus.UNKNOWN
          );
          break;
        case 'brandtrapOnbekend':
          filtered = filtered.filter(item =>
            item.fireStairStatus === FireStairStatus.UNKNOWN
          );
          break;
      }
    } else {
      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(item =>
          item.location.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply type filter
      const typeFilter = activeFilters.type || [];
      if (typeFilter.length > 0) {
        filtered = filtered.filter(item => typeFilter.includes(item.type));
      }
    }

    // Apply sorting
    return sortData(filtered, sortColumn, sortDirection);
  }, [fireSafetyInfo, activeActionableFilter, searchTerm, activeFilters, sortColumn, sortDirection, sortData]);

  // Handle sort
  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }, [sortColumn, sortDirection]);

  if (isLoading && fireSafetyInfo.length === 0) {
    return (
      <>
        <HelmetProvider>
          <Helmet>
            <title>Brandveiligheid | Onderhoud | Yally</title>
          </Helmet>
        </HelmetProvider>
        <div className="min-h-screen bg-background">
          <div className="container mx-auto py-6 px-4 lg:px-6">
            <div className="flex justify-center items-center h-64">
              <Spinner size="lg" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Brandveiligheid | Onderhoud | Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-6 px-4 lg:px-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brick-primary/10 rounded-lg">
                <Fire className="h-6 w-6 text-brick-primary" weight="duotone" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Brandveiligheid
                </h1>
                <p className="text-sm text-muted-foreground">
                  Overzicht van brandveiligheidsstatussen voor gebouwen en units
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={isLoading}
                className="gap-2"
              >
                <ArrowsClockwise
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  weight="bold"
                />
                Vernieuwen
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <FireSafetySummaryCards
            actionableIssues={actionableIssues}
            totalItems={totalItems}
            activeFilter={activeActionableFilter}
            onFilterClick={handleActionableIssueFilter}
          />

          {/* Filters using UniversalTableSearch */}
          <UniversalTableSearch
            rows={filteredInfo}
            searchValue={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              // Clear actionable filter when searching
              if (value) {
                setActiveActionableFilter(null);
                setHighlightedColumn(null);
              }
            }}
            searchPlaceholder="Zoek op locatie..."
            filterSections={FILTER_SECTIONS}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            isLoading={isLoading}
            showSpacer={false}
          />

          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-4">
            {filteredInfo.length} van {totalItems} items weergegeven
          </p>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-neutral-primary/10 mb-24">
            <FireSafetyTable
              data={filteredInfo}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              highlightedColumn={highlightedColumn}
              onSort={handleSort}
              onUpdateField={updateField}
              onCopyField={copyFieldToTargets}
              getStatusOptions={getStatusOptions}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BrandveiligheidPage;
